import {
  CSSProperties,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
  MouseEvent as ReactMouseEvent,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";

const TOOLTIP_OFFSET = 8;
const DEFAULT_Z_INDEX = 50;
const DEFAULT_POSITION: CSSProperties["position"] = "fixed";
const DEFAULT_STYLE: CSSProperties = {
  position: DEFAULT_POSITION,
  zIndex: DEFAULT_Z_INDEX,
  top: 0,
  left: 0,
  opacity: 0,
};

export type ArrowPlacement = "top" | "bottom" | "right";

type Props = {
  children: ReactNode;
  content: ReactNode;
  arrowPlacement?: ArrowPlacement;
  show?: boolean;
  displayOn?: "hover" | "click";
  toolTipClassName?: string;
  triggerClassName?: string;
  showArrow?: boolean;
  propagateEventOnTouch?: boolean;
};

export const Tooltip = ({
  children,
  content,
  arrowPlacement = "bottom",
  show = true,
  displayOn = "hover",
  toolTipClassName,
  triggerClassName,
  showArrow = true,
  propagateEventOnTouch = false,
}: Props) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [arrowOffset, setArrowOffset] = useState<number>(0);
  const [tooltipStyle, setTooltipStyle] =
    useState<CSSProperties>(DEFAULT_STYLE);

  const updateTooltipPosition = useCallback(() => {
    if (!tooltipRef.current || !triggerRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top: number;
    let left: number;

    if (arrowPlacement === "right") {
      left = triggerRect.right + TOOLTIP_OFFSET;
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
    } else {
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      if (arrowPlacement === "top") {
        top = triggerRect.bottom + TOOLTIP_OFFSET;
      } else {
        top = triggerRect.top - tooltipRect.height - TOOLTIP_OFFSET;
      }
    }

    const clampLeft = Math.max(
      TOOLTIP_OFFSET,
      Math.min(left, window.innerWidth - tooltipRect.width - TOOLTIP_OFFSET)
    );
    const clampTop = Math.max(
      TOOLTIP_OFFSET,
      Math.min(top, window.innerHeight - tooltipRect.height - TOOLTIP_OFFSET)
    );

    setTooltipStyle({
      ...DEFAULT_STYLE,
      top: clampTop,
      left: clampLeft,
      opacity: 1,
    });

    if (arrowPlacement === "right") {
      const triggerMiddle = triggerRect.top + triggerRect.height / 2;
      const arrowTop = triggerMiddle - clampTop;
      setArrowOffset(arrowTop);
    } else {
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const arrowLeft = triggerCenter - clampLeft;
      setArrowOffset(arrowLeft);
    }
  }, [arrowPlacement]);

  useLayoutEffect(() => {
    if (isVisible) {
      updateTooltipPosition();
    }
  }, [isVisible, arrowPlacement, updateTooltipPosition]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const handleMove = () => {
      if (!triggerRef.current) {
        return;
      }

      const rect = triggerRef.current.getBoundingClientRect();
      const width = window.innerWidth || document.documentElement.clientWidth;
      const height =
        window.innerHeight || document.documentElement.clientHeight;

      const inView =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= height &&
        rect.right <= width;

      if (inView && isVisible) {
        updateTooltipPosition();
      } else {
        setIsVisible(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        triggerRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleMove, true);
    window.addEventListener("resize", handleMove);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleMove, true);
      window.removeEventListener("resize", handleMove);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, updateTooltipPosition]);

  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return undefined;
    }

    const handleTriggerTouch = (e: TouchEvent) => {
      if (!propagateEventOnTouch) {
        e.preventDefault();
        e.stopPropagation();
      }
      setIsVisible((prev) => !prev);
    };

    trigger.addEventListener("touchstart", handleTriggerTouch, {
      passive: false,
    });

    return () => {
      trigger.removeEventListener("touchstart", handleTriggerTouch);
    };
  }, [propagateEventOnTouch]);

  const handleTriggerClick = () => {
    if (displayOn === "click") {
      setIsVisible((prev) => !prev);
    }
  };

  const handleTriggerHover = (e: ReactMouseEvent) => {
    if (displayOn === "hover") {
      if (e.type === "mouseenter") {
        setIsVisible(true);
      }
      if (e.type === "mouseleave") {
        setIsVisible(false);
      }
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        onClick={handleTriggerClick}
        onMouseEnter={handleTriggerHover}
        onMouseLeave={handleTriggerHover}
        className={triggerClassName}
        onKeyDown={(e) => {
          e.preventDefault();
          if (e.key === "Enter") {
            handleTriggerClick();
          }
        }}
      >
        {children}
      </div>

      {isVisible &&
        show &&
        createPortal(
          <div
            ref={tooltipRef}
            style={tooltipStyle}
            className={classNames(
              "relative inline-block max-w-xs text-sm font-semibold text-primary-500 pointer-events-none transition-opacity duration-150",
              toolTipClassName
            )}
          >
            <div className="px-4 py-3 shadow-elevation-03 bg-gray-800 text-white rounded-md">
              {content}
            </div>

            {showArrow && (
              <div
                className={classNames(
                  "absolute w-2 h-2 rotate-45 bg-gray-800",
                  {
                    "-top-1 left-1/2 -translate-x-1/2":
                      arrowPlacement === "top",
                    "-bottom-1 left-1/2 -translate-x-1/2":
                      arrowPlacement === "bottom",
                    "-left-1 top-1/2 -translate-y-1/2":
                      arrowPlacement === "right",
                  }
                )}
                style={
                  arrowPlacement === "right"
                    ? { top: arrowOffset }
                    : { left: arrowOffset }
                }
              />
            )}
          </div>,
          document.body
        )}
    </>
  );
};
